import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { createHub, updateHub } from "../redux/hubSlice";
import { toast } from "react-toastify";
import "quill/dist/quill.snow.css";
import Quill from "quill";
import sanitizeHtml from "sanitize-html"; // Import sanitize-html

const PostHubForm = ({ editMode = false, existingHub = {}, onClose }) => {
  const dispatch = useDispatch();
  const quillRef = useRef(null);
  const [quillInstance, setQuillInstance] = useState(null);

  const [form, setForm] = useState({
    title: editMode ? existingHub.title || "" : "",
    description: editMode ? existingHub.description || "" : "",
    image: null,
    imageUrl: editMode ? existingHub.imageUrl || "" : "",
  });

  // Initialize Quill editor
  useEffect(() => {
    if (!quillInstance) {
      const quill = new Quill("#quill-editor", {
        theme: "snow",
        placeholder: "Enter hub description...",
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link"],
            ["clean"],
          ],
        },
      });

      // Update description in form state on content change
      quill.on("text-change", () => {
        setForm((prevForm) => ({
          ...prevForm,
          description: quill.root.innerHTML,
        }));
      });

      setQuillInstance(quill);
    }
  }, [quillInstance]);

  // Populate Quill editor in edit mode
  useEffect(() => {
    if (quillInstance && editMode && existingHub.description) {
      quillInstance.root.innerHTML = existingHub.description;
    }
  }, [editMode, existingHub, quillInstance]);

  // Handle input changes for title and image
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    setForm((prevForm) => ({
      ...prevForm,
      [name]: files ? files[0] : value,
    }));
  };

  // Sanitize description before submitting
  const sanitizeDescription = (html) => {
    return sanitizeHtml(html, {
      allowedTags: ["b", "i", "u", "p", "ul", "ol", "li", "a", "h1", "h2", "br"],
      allowedAttributes: {
        a: ["href", "target"],
      },
      allowedStyles: {
        "*": {
          "text-align": [/^left$/, /^right$/, /^center$/],
          color: [/^#[0-9a-fA-F]{3,6}$/], // Allow hex colors
        },
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required.");
      return;
    }

    const sanitizedDescription = sanitizeDescription(form.description);

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", sanitizedDescription);

    if (form.image) {
      formData.append("image", form.image);
    } else if (editMode) {
      formData.append("imageUrl", form.imageUrl);
    }

    if (editMode) {
      dispatch(updateHub({ id: existingHub.id, hubData: formData }))
        .unwrap()
        .then(() => {
          toast.success("Hub updated successfully!");
          onClose && onClose();
        })
        .catch(() => toast.error("Failed to update hub"));
    } else {
      dispatch(createHub(formData))
        .unwrap()
        .then(() => {
          toast.success("Hub created successfully!");
          setForm({
            title: "",
            description: "",
            image: null,
            imageUrl: "",
          });
          if (quillInstance) quillInstance.root.innerHTML = "";
        })
        .catch(() => toast.error("Failed to create hub"));
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {/* Title Input */}
      <input
        type="text"
        name="title"
        placeholder="Hub Title"
        value={form.title}
        onChange={handleChange}
        className="w-full p-3 border rounded-md"
      />

      {/* Description Quill Editor */}
      <div id="quill-editor" className="w-full h-40 border rounded-md"></div>

      {/* Image Upload */}
      <input
        type="file"
        name="image"
        accept="image/*"
        onChange={handleChange}
        className="w-full p-3 border rounded-md"
      />
      {editMode && form.imageUrl && (
        <div className="flex items-center space-x-4">
          <img
            src={form.imageUrl}
            alt="Current"
            className="w-16 h-16 object-cover rounded-md"
          />
          <span className="text-sm text-gray-500">Current Image</span>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        className="bg-blue-500 text-white py-2 px-4 rounded"
      >
        {editMode ? "Update Hub" : "Create Hub"}
      </button>
    </form>
  );
};

export default PostHubForm;
